/* ============================================================================
   mgai-a11y — MARGI accessibility preferences widget (dependency-free)

   SCOPE FENCE (non-negotiable): this script is a USER-PREFERENCE COMFORT TOOL.
   It toggles CSS classes on <html> and renders its own control surface. It does
   NOT mutate content DOM, inject alt text, rewrite ARIA on page elements, fix
   heading order, or claim conformance. The FTC fined accessiBe $1M in April
   2025 for exactly those claims. Compliance with IS 5568 / WCAG 2.0 AA comes
   from the theme's own HTML.

   Loads deferred. Persisted preferences are applied earlier and independently
   by the inline bootstrap in snippets/mgai-a11y.liquid, so a user who needs
   150% text still gets it if this file fails to load.
   ============================================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'mgai_a11y_prefs_v1';
  var VERSION = 1;
  /* Must match the guard in the inline bootstrap. */
  var CLASS_SAFE = /^mgai-[a-z0-9-]+$/;

  if (window.__mgaiA11yLoaded) return;
  window.__mgaiA11yLoaded = true;

  var CFG = window.MGAI_A11Y || {};
  var T = CFG.strings || {};
  var DIR = CFG.dir === 'rtl' ? 'rtl' : 'ltr';

  /* -------------------------------------------------------------------------
     Preferences
  ------------------------------------------------------------------------- */
  var DEFAULTS = {
    links: false,
    contrast: 'off',
    textSize: 100,
    lineSpacing: 'normal',
    readableFont: false,
    headings: false,
    cursorBlack: false,
    cursorLarge: false,
    reduceMotion: false
  };

  var CONTRAST_CYCLE = ['off', 'high', 'invert', 'mono'];
  var TEXT_CYCLE = [100, 115, 130, 150];
  var LINES_CYCLE = ['normal', '16', '20'];

  function cycle(list, current) {
    var i = list.indexOf(current);
    return list[(i < 0 ? 0 : i + 1) % list.length];
  }

  /* SINGLE SOURCE OF TRUTH for the class vocabulary. The inline bootstrap does
     not duplicate these rules — it replays the class list persisted alongside
     the preferences, so the two can never drift. */
  var CLASS_RULES = [
    ['mgai-links', function (p) { return !!p.links; }],
    ['mgai-contrast-high', function (p) { return p.contrast === 'high'; }],
    ['mgai-contrast-invert', function (p) { return p.contrast === 'invert'; }],
    ['mgai-contrast-mono', function (p) { return p.contrast === 'mono'; }],
    ['mgai-text-115', function (p) { return p.textSize === 115; }],
    ['mgai-text-130', function (p) { return p.textSize === 130; }],
    ['mgai-text-150', function (p) { return p.textSize === 150; }],
    ['mgai-lines-16', function (p) { return p.lineSpacing === '16'; }],
    ['mgai-lines-20', function (p) { return p.lineSpacing === '20'; }],
    ['mgai-readable-font', function (p) { return !!p.readableFont; }],
    ['mgai-headings', function (p) { return !!p.headings; }],
    ['mgai-cursor-black', function (p) { return !!p.cursorBlack; }],
    ['mgai-cursor-large', function (p) { return !!p.cursorLarge; }],
    ['mgai-reduce-motion', function (p) { return !!p.reduceMotion; }]
  ];

  function classesFor(p) {
    var out = [];
    for (var i = 0; i < CLASS_RULES.length; i++) {
      if (CLASS_RULES[i][1](p)) out.push(CLASS_RULES[i][0]);
    }
    return out;
  }

  function isAnyActive(p) {
    return classesFor(p).length > 0;
  }

  var prefs = readStorage();

  function readStorage() {
    var merged = {};
    for (var k in DEFAULTS) merged[k] = DEFAULTS[k];
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return merged;
      var saved = JSON.parse(raw);
      if (!saved || saved.v !== VERSION || !saved.p) return merged;
      for (var key in DEFAULTS) {
        if (Object.prototype.hasOwnProperty.call(saved.p, key)) merged[key] = saved.p[key];
      }
    } catch (e) { /* private mode / quota / corrupt value → defaults */ }
    return merged;
  }

  function writeStorage(p) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: VERSION, p: p, c: classesFor(p) }));
    } catch (e) { /* quota or private mode: session stays in memory only */ }
  }

  /* Reconciles every class in the vocabulary, so a class left over from an
     older release (or from a tampered localStorage) is removed, not orphaned. */
  function applyClasses(p) {
    var root = document.documentElement;
    for (var i = 0; i < CLASS_RULES.length; i++) {
      var cls = CLASS_RULES[i][0];
      if (!CLASS_SAFE.test(cls)) continue;
      if (CLASS_RULES[i][1](p)) root.classList.add(cls);
      else root.classList.remove(cls);
    }
  }

  function setPref(patch, announcement) {
    for (var k in patch) prefs[k] = patch[k];
    applyClasses(prefs);
    writeStorage(prefs);
    render();
    if (announcement) announce(announcement);
  }

  function resetPrefs() {
    for (var k in DEFAULTS) prefs[k] = DEFAULTS[k];
    applyClasses(prefs);
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    render();
    announce(T.resetDone || T.reset || 'Reset');
  }

  /* -------------------------------------------------------------------------
     Icons (inline SVG, 24x24, currentColor)
  ------------------------------------------------------------------------- */
  var ICONS = {
    accessibility: '<circle cx="16" cy="4" r="1"/><path d="m18 19 1-7-6 1"/><path d="m5 8 3-3 5.5 3-2.36 3.5"/><path d="M4.24 14.5a5 5 0 0 0 6.88 6"/><path d="M13.76 17.5a5 5 0 0 0-6.88-6"/>',
    link: '<path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 1 1 0 10h-2"/><line x1="8" y1="12" x2="16" y2="12"/>',
    contrast: '<circle cx="12" cy="12" r="10"/><path d="M12 18a6 6 0 0 0 0-12z" fill="currentColor"/>',
    text: '<polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/>',
    lines: '<line x1="3" y1="5" x2="21" y2="5"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="19" x2="21" y2="19"/>',
    font: '<path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/>',
    heading: '<path d="M6 12h12"/><path d="M6 20V4"/><path d="M18 20V4"/>',
    pointer: '<path d="m3.7 3a.5.5 0 0 0-.7.7l6.5 16a.5.5 0 0 0 .95-.06l1.57-6.08a2 2 0 0 1 1.44-1.48l6.13-1.58a.5.5 0 0 0 .06-.95z" fill="currentColor"/>',
    pointerLarge: '<path d="m2.6 2a.4.4 0 0 0-.56.56l5.2 12.8a.4.4 0 0 0 .76-.05l1.26-4.87a1.6 1.6 0 0 1 1.15-1.18l4.9-1.27a.4.4 0 0 0 .05-.76z" fill="currentColor"/><polyline points="16 14 21 14 21 19"/><polyline points="14 21 14 16 19 16"/><line x1="15" y1="15" x2="21" y2="21"/>',
    pause: '<rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>',
    reset: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
    close: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>'
  };

  function svg(name) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      ICONS[name] + '</svg>';
  }

  /* -------------------------------------------------------------------------
     Control definitions
  ------------------------------------------------------------------------- */
  var L = T.labels || {};
  var V = T.values || {};

  /* `cycling: true` controls OMIT aria-pressed — a three- or four-state control
     is not a binary toggle, and the accessible name carries the value instead. */
  var CONTROLS = [
    { key: 'links', icon: 'link', label: L.links,
      on: function () { return prefs.links; },
      act: function () { setPref({ links: !prefs.links }, L.links + ': ' + (prefs.links ? (T.off || 'off') : (T.on || 'on'))); } },

    { key: 'contrast', icon: 'contrast', label: L.contrast, cycling: true,
      on: function () { return prefs.contrast !== 'off'; },
      value: function () { return (V.contrast || {})[prefs.contrast]; },
      act: function () {
        var next = cycle(CONTRAST_CYCLE, prefs.contrast);
        setPref({ contrast: next }, L.contrast + ': ' + (V.contrast || {})[next]);
      } },

    { key: 'textSize', icon: 'text', label: L.textSize, cycling: true,
      on: function () { return prefs.textSize !== 100; },
      value: function () { return prefs.textSize + '%'; },
      act: function () {
        var next = cycle(TEXT_CYCLE, prefs.textSize);
        setPref({ textSize: next }, L.textSize + ': ' + next + '%');
      } },

    { key: 'lineSpacing', icon: 'lines', label: L.lineSpacing, cycling: true,
      on: function () { return prefs.lineSpacing !== 'normal'; },
      value: function () { return (V.lineSpacing || {})[prefs.lineSpacing]; },
      act: function () {
        var next = cycle(LINES_CYCLE, prefs.lineSpacing);
        setPref({ lineSpacing: next }, L.lineSpacing + ': ' + (V.lineSpacing || {})[next]);
      } },

    { key: 'readableFont', icon: 'font', label: L.readableFont,
      on: function () { return prefs.readableFont; },
      act: function () { setPref({ readableFont: !prefs.readableFont }, L.readableFont + ': ' + (prefs.readableFont ? (T.off || 'off') : (T.on || 'on'))); } },

    { key: 'headings', icon: 'heading', label: L.headings,
      on: function () { return prefs.headings; },
      act: function () { setPref({ headings: !prefs.headings }, L.headings + ': ' + (prefs.headings ? (T.off || 'off') : (T.on || 'on'))); } },

    { key: 'cursorBlack', icon: 'pointer', label: L.cursorBlack,
      on: function () { return prefs.cursorBlack; },
      act: function () { setPref({ cursorBlack: !prefs.cursorBlack }, L.cursorBlack + ': ' + (prefs.cursorBlack ? (T.off || 'off') : (T.on || 'on'))); } },

    { key: 'cursorLarge', icon: 'pointerLarge', label: L.cursorLarge,
      on: function () { return prefs.cursorLarge; },
      act: function () { setPref({ cursorLarge: !prefs.cursorLarge }, L.cursorLarge + ': ' + (prefs.cursorLarge ? (T.off || 'off') : (T.on || 'on'))); } },

    { key: 'reduceMotion', icon: 'pause', label: L.reduceMotion,
      on: function () { return prefs.reduceMotion; },
      act: function () { setPref({ reduceMotion: !prefs.reduceMotion }, L.reduceMotion + ': ' + (prefs.reduceMotion ? (T.off || 'off') : (T.on || 'on'))); } }
  ];

  /* -------------------------------------------------------------------------
     DOM
  ------------------------------------------------------------------------- */
  var root, fab, backdrop, panel, live, tiles = {};
  var isOpen = false;
  var lastFocus = null;

  function el(tag, attrs, html) {
    var node = document.createElement(tag);
    if (attrs) for (var a in attrs) {
      if (attrs[a] === null || attrs[a] === undefined) continue;
      node.setAttribute(a, attrs[a]);
    }
    if (html != null) node.innerHTML = html;
    return node;
  }

  function build() {
    /* data-side is the physical edge, already resolved from the operator's
       "left"/"right" by the Liquid snippet — the CSS only knows start/end. */
    root = el('div', {
      id: 'mgai-a11y-root',
      dir: DIR,
      lang: CFG.lang || 'he',
      'data-side': CFG.side === 'end' ? 'end' : 'start'
    });

    /* Live region lives OUTSIDE the panel: announcements made while closing
       would otherwise be lost with the panel's subtree. */
    live = el('div', { class: 'mgai-sr', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });

    fab = el('button', {
      type: 'button',
      class: 'mgai-fab',
      id: 'mgai-a11y-trigger',
      'aria-label': T.triggerLabel || 'Accessibility options',
      'aria-expanded': 'false',
      'aria-controls': 'mgai-a11y-panel',
      'aria-keyshortcuts': 'Alt+A'
    }, svg('accessibility'));
    fab.addEventListener('click', function () { togglePanel(); });

    backdrop = el('div', { class: 'mgai-backdrop', hidden: 'hidden' });
    backdrop.addEventListener('click', function () { closePanel(); });

    panel = el('div', {
      class: 'mgai-panel',
      id: 'mgai-a11y-panel',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'mgai-a11y-title',
      hidden: 'hidden'
    });

    var head = el('div', { class: 'mgai-head' });
    head.appendChild(el('h2', { class: 'mgai-title', id: 'mgai-a11y-title' }, esc(T.title || 'Accessibility options')));
    var closeBtn = el('button', { type: 'button', class: 'mgai-close', 'aria-label': T.close || 'Close' }, svg('close'));
    closeBtn.addEventListener('click', function () { closePanel(); });
    head.appendChild(closeBtn);
    panel.appendChild(head);

    var body = el('div', { class: 'mgai-body' });
    var grid = el('div', { class: 'mgai-grid' });

    CONTROLS.forEach(function (c) {
      var btn = el('button', { type: 'button', class: 'mgai-tile', 'data-key': c.key });
      btn.innerHTML = svg(c.icon) +
        '<span class="mgai-tile-label">' + esc(c.label || c.key) + '</span>' +
        '<span class="mgai-tile-value"></span>';
      btn.addEventListener('click', function () { c.act(); });
      tiles[c.key] = btn;
      grid.appendChild(btn);
    });
    body.appendChild(grid);

    var reset = el('button', { type: 'button', class: 'mgai-reset' }, svg('reset') + ' ' + esc(T.reset || 'Reset'));
    reset.addEventListener('click', function () { resetPrefs(); });
    body.appendChild(reset);

    var foot = el('div', { class: 'mgai-foot' });
    /* Honest limits, stated in the tool itself — no compliance badge. */
    foot.appendChild(el('p', null, esc(T.disclaimer || '')));
    if (CFG.statementUrl) {
      var p = el('p');
      p.appendChild(el('a', { class: 'mgai-link', href: CFG.statementUrl }, esc(T.statement || 'Accessibility statement')));
      foot.appendChild(p);
    }
    var hint = el('p');
    hint.innerHTML = esc(T.keyboardHint || '') + ' <span class="mgai-kbd">Alt</span> + <span class="mgai-kbd">A</span>';
    foot.appendChild(hint);
    body.appendChild(foot);

    panel.appendChild(body);

    root.appendChild(live);
    root.appendChild(fab);
    root.appendChild(backdrop);
    root.appendChild(panel);
    document.body.appendChild(root);
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function render() {
    if (!root) return;
    CONTROLS.forEach(function (c) {
      var btn = tiles[c.key];
      if (!btn) return;
      var on = !!c.on();
      btn.setAttribute('data-on', on ? 'true' : 'false');
      var valueText = c.value ? (c.value() || '') : '';
      btn.querySelector('.mgai-tile-value').textContent = c.cycling ? valueText : '';
      if (c.cycling) {
        btn.removeAttribute('aria-pressed');
        btn.setAttribute('aria-label', (c.label || c.key) + (valueText ? ': ' + valueText : ''));
      } else {
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.setAttribute('aria-label', c.label || c.key);
      }
    });
    fab.setAttribute('data-active', isAnyActive(prefs) ? 'true' : 'false');
  }

  var announceTimer = null;
  function announce(msg) {
    if (!live) return;
    live.textContent = '';
    if (announceTimer) clearTimeout(announceTimer);
    announceTimer = setTimeout(function () { live.textContent = msg; }, 60);
  }

  /* -------------------------------------------------------------------------
     Open / close + focus management
  ------------------------------------------------------------------------- */
  function focusables() {
    return Array.prototype.filter.call(
      panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null || n === document.activeElement; }
    );
  }

  function openPanel() {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    backdrop.removeAttribute('hidden');
    panel.removeAttribute('hidden');
    fab.setAttribute('aria-expanded', 'true');
    var f = focusables();
    (f[0] || panel).focus();
  }

  function closePanel() {
    if (!isOpen) return;
    isOpen = false;
    backdrop.setAttribute('hidden', 'hidden');
    panel.setAttribute('hidden', 'hidden');
    fab.setAttribute('aria-expanded', 'false');
    /* Opened by Alt+A from nowhere in particular, activeElement is <body>.
       Restoring "focus" to <body> silently drops the user at the top of the
       document on their next Tab, so fall back to the trigger instead. */
    var restore = (lastFocus && lastFocus !== document.body && document.contains(lastFocus))
      ? lastFocus : fab;
    restore.focus();
  }

  function togglePanel() { isOpen ? closePanel() : openPanel(); }

  function onKeydown(e) {
    /* e.code, not e.key: on macOS Alt+A produces the dead key 'å', so an
       e.key === 'a' check silently never fires. */
    if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === 'KeyA') {
      e.preventDefault();
      togglePanel();
      return;
    }
    if (!isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
      return;
    }
    if (e.key === 'Tab') {
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (panel.contains(document.activeElement) === false) { e.preventDefault(); first.focus(); }
    }
  }

  /* -------------------------------------------------------------------------
     Init
  ------------------------------------------------------------------------- */
  function init() {
    /* Safety net: the inline bootstrap may have been blocked (localStorage
       disabled, CSP). Re-apply from whatever we could read. */
    applyClasses(prefs);
    build();
    render();
    document.addEventListener('keydown', onKeydown, true);
    window.addEventListener('storage', function (e) {
      if (e.key !== STORAGE_KEY) return;
      prefs = readStorage();
      applyClasses(prefs);
      render();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
